import React, {useEffect, useMemo, useState} from "react";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import {Skeleton} from "primereact/skeleton";
import {Tag} from "primereact/tag";
import {personalFocusApi} from "../../../models/home/personal-focus-api";
import {STATUS, count, faDay, rangeOf} from "./weekly-focus-labels";

const TABS = [
    {value: "milestone", label: "مایلستون‌ها"},
    {value: "project", label: "پروژه‌ها"},
    {value: "my_tasks", label: "کارهای من"},
];

// «این هفته روی چه چیزی تمرکز می‌کنی؟» — only what the person really takes part in: milestones with one of their
// own tasks, projects they may see, and their own tasks (docs/tasks/31-personal-focus.md). One suggestion at most.
export default function FocusPickerDialog({visible, workspaceId, range, current, pending, onHide, onApply}) {
    const [options, setOptions] = useState(null);
    const [error, setError] = useState(false);
    const [tab, setTab] = useState("milestone");
    const [query, setQuery] = useState("");
    const [choice, setChoice] = useState(null);

    useEffect(() => {
        if (!visible || !workspaceId) return undefined;
        let alive = true;
        setOptions(null);
        setError(false);
        setQuery("");
        setChoice(current?.scopeType ? {type: current.scopeType, key: current.spotlight?.key || null} : null);
        personalFocusApi.options(workspaceId, range).then((result) => {
            if (!alive) return;
            setOptions(result);
            setTab(current?.scopeType || (result?.milestones?.length ? "milestone" : "project"));
        }).catch(() => alive && setError(true));
        return () => { alive = false; };
    }, [visible, workspaceId, range, current]);

    const list = useMemo(() => {
        const items = tab === "milestone" ? options?.milestones : tab === "project" ? options?.projects : [];
        const words = query.trim().toLowerCase();
        return (items || []).filter((item) => !words || [item.title, item.project?.title, item.goal?.title].filter(Boolean).join(" ").toLowerCase().includes(words));
    }, [options, tab, query]);

    const isChosen = (type, key) => choice?.type === type && (type === "my_tasks" || choice?.key === key);
    const footer = <div className="flex flex-column-reverse sm:flex-row sm:justify-content-end gap-2">
        <Button type="button" outlined severity="secondary" className="text-xs justify-content-center" label="انصراف" disabled={pending} onClick={onHide}/>
        <Button type="button" className="text-xs justify-content-center" icon="pi pi-check" label="انتخاب و دیدن کارها" loading={pending} disabled={!choice || pending} onClick={() => onApply({scopeType: choice.type, targetId: choice.type === "my_tasks" ? null : choice.key, rangeKind: range})}/>
    </div>;

    return <Dialog visible={visible} modal dismissableMask={!pending} draggable={false} header={`${rangeOf(range).phrase} روی چه چیزی تمرکز می‌کنی؟`} footer={footer} className="rtl shabnam-font wf-picker w-11 sm:w-32rem border-round-xl" onHide={onHide}>
        <div className="flex flex-column gap-3">
            <p className="text-xs text-500 line-height-3 m-0">فقط مواردی که در آن‌ها کار داری یا عضوشان هستی{options?.endDate ? ` · تا ${faDay(options.endDate)}` : ""}</p>
            <span className="wf-search relative block w-full">
                <i className="pi pi-search" aria-hidden="true"/>
                <InputText value={query} className="w-full text-sm" placeholder="جست‌وجوی مایلستون، پروژه یا هدف" aria-label="جست‌وجو" onChange={(event) => setQuery(event.target.value)}/>
            </span>
            <div className="flex gap-2" role="tablist">
                {TABS.map((item) => <button key={item.value} type="button" role="tab" aria-selected={tab === item.value} className={`wf-tab ${tab === item.value ? "active" : ""}`} onClick={() => setTab(item.value)}>{item.label}</button>)}
            </div>
            <div className="wf-choices flex flex-column gap-2" role="radiogroup" aria-label="گزینه‌های تمرکز">
                {error ? <span className="text-xs text-red-500 p-3">گزینه‌ها بارگذاری نشد. دوباره تلاش کن.</span>
                    : !options ? [1, 2, 3].map((item) => <Skeleton key={item} height="3.5rem" className="border-round-lg"/>)
                    : tab === "my_tasks" ? <Choice chosen={isChosen("my_tasks")} title="کارهای محول‌شده به من" path={`کارهای تو با موعد تا پایان این بازه (${count(options.myTasks?.facts?.count || 0)} کار${options.myTasks?.facts?.late ? ` · ${count(options.myTasks.facts.late)} عقب‌افتاده` : ""}) · رابطه‌ی پروژه و مایلستون عوض نمی‌شود`} onSelect={() => setChoice({type: "my_tasks"})}/>
                    : list.length ? list.map((item) => <Choice key={item.key} chosen={isChosen(item.type, item.key)} title={item.title} status={item.status} suggested={item.suggested}
                        path={[item.project?.title && `پروژه: ${item.project.title}`, item.goal?.title && `هدف: ${item.goal.title}`, item.lastDue && `آخرین موعد: ${faDay(item.lastDue)}`, item.dueDate && `موعد: ${faDay(item.dueDate)}`, item.facts.count ? `${count(item.facts.count)} کار تو${item.facts.late ? ` · ${count(item.facts.late)} عقب‌افتاده` : ""}` : "کاری به تو محول نشده"].filter(Boolean).join(" · ")}
                        onSelect={() => setChoice({type: item.type, key: item.key})}/>)
                    : <span className="text-xs text-500 text-center p-4">{tab === "milestone" ? "مایلستونی با کار محول‌شده به تو پیدا نشد." : "پروژه‌ای پیدا نشد."}</span>}
            </div>
        </div>
    </Dialog>;
}

function Choice({chosen, title, path, status, suggested, onSelect}) {
    const pill = STATUS[status];
    return <button type="button" role="radio" aria-checked={chosen} className={`wf-choice flex align-items-center gap-3 w-full text-right p-3 border-round-lg ${chosen ? "selected" : ""}`} onClick={onSelect}>
        <span className="wf-radio flex-shrink-0" aria-hidden="true"/>
        <span className="flex-1 min-w-0">
            <span className="flex flex-wrap align-items-center gap-2"><strong className="text-sm text-900 line-height-2">{title}</strong>{suggested && <span className="wf-suggest">پیشنهاد</span>}</span>
            <small className="block text-xs text-500 line-height-3 mt-1">{path}</small>
        </span>
        {pill && <Tag severity={pill.severity} className="text-2xs font-normal flex-shrink-0" value={pill.label}/>}
    </button>;
}
